import type { z } from "zod";

export type MotionRole = "transition" | "animation";

/** `in` plays as the element enters, `out` as it leaves. */
export type MotionDirection = "in" | "out";

/** CSS-ready style for one frame. `reveal` (0..1) drives text/number-aware renderers (typewriter, count-up). */
export interface MotionFrame {
	opacity: number;
	transform?: string;
	filter?: string;
	clipPath?: string;
	reveal: number;
}

export interface MotionRenderInput<TParams> {
	frame: number;
	durationInFrames: number;
	direction: MotionDirection;
	params: TParams;
}

/**
 * Reduced motion (`prefers-reduced-motion`): either an opacity-only fade capped at 150 ms, or
 * nothing (`none`: the element is shown at rest).
 */
export type ReducedMotionFallback = "opacity" | "none";

export const REDUCED_MOTION_MAX_MS = 150;

export interface MotionKindDefinition<
	TKey extends string = string,
	TParams extends Record<string, unknown> = Record<string, unknown>,
> {
	key: TKey;
	role: MotionRole;
	label: string;
	paramsSchema: z.ZodType<TParams>;
	defaultParams: TParams;
	/** Used when no layer sets a duration. 0 for kinds that take no time (cut / none). */
	defaultDurationMs: number;
	/** Cards format: a finger-driven swipe can play it (D11). Transitions only. */
	gestureCapable: boolean;
	reducedMotionFallback: ReducedMotionFallback;
	/** Deterministic: same input, same output. Frame 0..durationInFrames. */
	render(input: MotionRenderInput<TParams>): MotionFrame;
}

/** Params are inferred from `paramsSchema`; defaults and render must match it. */
export function defineMotionKind<
	TKey extends string,
	TParams extends Record<string, unknown>,
>(
	definition: Omit<
		MotionKindDefinition<TKey, TParams>,
		"defaultParams" | "render"
	> & {
		defaultParams: NoInfer<TParams>;
		render(input: MotionRenderInput<NoInfer<TParams>>): MotionFrame;
	},
): MotionKindDefinition<TKey, TParams> {
	return definition;
}

export const MOTION_AT_REST: MotionFrame = { opacity: 1, reveal: 1 };

/** 0..1 progress toward "at rest": `in` runs 0→1, `out` runs 1→0. */
export function motionProgress(input: {
	frame: number;
	durationInFrames: number;
	direction: MotionDirection;
}): number {
	if (input.durationInFrames <= 0) {
		return input.direction === "in" ? 1 : 0;
	}
	const raw = Math.min(1, Math.max(0, input.frame / input.durationInFrames));
	return input.direction === "in" ? raw : 1 - raw;
}
